// services/interestTagsService.ts
// Client for the interest-tag resolution endpoints (prior-stack Phase 3/4).
import { typedApi } from './typedApiClient';

export interface ResolvedCourse {
  code: string;
  score: number;
  why: string;
  removed?: boolean;
}

export interface StarterChip {
  label: string;
  richness: number;
}

export interface InterestTagsData {
  interest_tags: string[];
  avoid_tags: string[];
  resolved: Record<string, ResolvedCourse[]>;
  starter_chips: StarterChip[];
}

interface RawResolvedCourse {
  code?: string | null;
  score?: number | null;
  why?: string | null;
  removed?: boolean | null;
}

/**
 * The generated payload types every field optional+nullable, and the per-tag
 * map's values land as `unknown` (the Pydantic->Marshmallow bridge does not
 * carry Dict value types through). Normalize both here, once.
 */
const toResolvedCourses = (courses: unknown): ResolvedCourse[] => {
  if (!Array.isArray(courses)) return [];
  return (courses as RawResolvedCourse[]).map((c) => ({
    code: c?.code ?? '',
    score: c?.score ?? 0,
    why: c?.why ?? '',
    ...(c?.removed == null ? {} : { removed: c.removed }),
  }));
};

export const interestTagsService = {
  async get(): Promise<InterestTagsData> {
    const data = await typedApi.studentProfile.getInterestTags();
    const resolved: Record<string, ResolvedCourse[]> = {};
    for (const [tag, courses] of Object.entries(data?.resolved ?? {})) {
      resolved[tag] = toResolvedCourses(courses);
    }
    return {
      interest_tags: [...(data?.interest_tags ?? [])],
      avoid_tags: [...(data?.avoid_tags ?? [])],
      resolved,
      starter_chips: (data?.starter_chips ?? []).map((chip) => ({
        label: chip.label ?? '',
        richness: chip.richness ?? 0,
      })),
    };
  },

  async update(
    interestTags: string[],
    avoidTags: string[]
  ): Promise<{ interest_tags: string[]; avoid_tags: string[] }> {
    const data = await typedApi.studentProfile.updateInterestTags({
      interest_tags: interestTags,
      avoid_tags: avoidTags,
    });
    return {
      interest_tags: [...(data?.interest_tags ?? [])],
      avoid_tags: [...(data?.avoid_tags ?? [])],
    };
  },

  async resolve(tag: string): Promise<{ tag: string; resolved: ResolvedCourse[] }> {
    const data = await typedApi.studentProfile.resolveInterestTag({ tag });
    return { tag: data?.tag ?? tag, resolved: toResolvedCourses(data?.resolved) };
  },

  async curate(tag: string, courseCode: string, action: 'remove' | 'restore'): Promise<void> {
    await typedApi.studentProfile.curateInterestTag({
      tag,
      course_code: courseCode,
      action,
    });
  },
};
